import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Banner } from "@companion-core/shared/app/Components/Banner";
import { Button } from "@companion-core/shared/app/Components/Button";
import { ValidationBanner } from "@companion-core/shared/app/Components/ValidationBanner";
import { usePass, usePDF } from "@companion-core/shared/app/Queries";
import { useI18nStore } from "@companion-core/shared/app/Store/i18nStore";
import { CardMemberLayoutProps } from "@companion-core/shared/app/Components/Blocks/Layouts/CardMemberLayout/cardMemberLayout.model";
import { getCloudinaryOptimizedUrl } from "@companion-core/shared/app/Utils/cloudinary";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { redirectTo } from "@companion-core/web/src/Utilities/navigation";
import { sanitizeHtml } from "@companion-core/shared/app/Utils/htmlSanitizer";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";
import { isAppleDevice } from "@companion-core/shared/app/Utils/platform";
import { Spin } from "antd";
import "Assets/Styles/scss/cardMemberLayout.scss";

export const CardMemberLayout = ({
  title,
  description,
  mediaUrl,
  rectoUrl,
  versoUrl,
  fullName,
  company,
  expirationDate,
  subscriptionExternalId,
}: CardMemberLayoutProps) => {
  const { t } = useTranslation();
  const { locale } = useI18nStore();
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const { refetch: fetchPDF, status: fetchPDFStatus } = usePDF(locale, false);
  const { refetch: fetchPass, status: fetchPassStatus } = usePass(false);
  const [isDownloadPdfLoading, setIsDownloadPdfLoading] = useState<boolean>(false);
  const [isAddToWalletLoading, setIsAddToWalletLoading] = useState<boolean>(false);
  const { branding } = useConfigStore((state) => ({
    branding: state.branding,
  }));
  const walletButton = useMemo(
    () => branding?.theme?.assets?.[`appleWallet-${locale}`],
    [branding, locale]
  );

  const downloadPDF = useCallback(async () => {
    setIsDownloadPdfLoading(true);
    fetchPDF()
      .then((res) => {
        setIsDownloadPdfLoading(false);
        const fileURL = URL.createObjectURL(
          new Blob([res.data?.data], {
            type: "application/pdf",
          })
        );
        redirectTo(fileURL, "file", undefined, "_blank");
      })
      .catch(() => setIsDownloadPdfLoading(false));
  }, []);

  const addToWallet = async () => {
    setIsAddToWalletLoading(true);
    fetchPass()
      .then(async (response) => {
        setIsAddToWalletLoading(false);
        const passBlob = new Blob([response.data?.data], {
          type: "application/vnd.apple.pkpass",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(passBlob);
        link.download = "my-pass.pkpass";
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      })
      .catch(() => setIsAddToWalletLoading(false));
  };

  return (
    <div data-testid="card-member-layout" className="card-member-layout">
      {!isMobileSize && (
        <div data-testid="card-member-layout__banner" className="card-member-layout__banner">
          <Banner title={title} imageUrl={mediaUrl} boxShadow={true} disappearOnMobileSize />
        </div>
      )}
      <div className="card-member-layout__content">
        <ValidationBanner
          testID={`card-member-layout__pdf-status-${fetchPDFStatus}`}
          visible={fetchPDFStatus === "error"}
          text={t("common.card-member.download.pdf.error")}
          type="error"
          autoFadeOut
          fadeOutDelay={10000}
        />
        <ValidationBanner
          testID={`card-member-layout__pass-status-${fetchPassStatus}`}
          visible={fetchPassStatus === "error"}
          text={t("common.card-member.download.pkpass.error")}
          type="error"
          autoFadeOut
          fadeOutDelay={10000}
        />
        {isMobileSize && title && (
          <h2
            data-testid="card-member-layout__title"
            className="card-member-layout__title super-title-3">
            {title}
          </h2>
        )}
        <div
          data-testid="card-member-layout__card"
          className="card-member-layout__card body-text-7">
          <div data-testid="card-member-layout__recto" className="card-member-layout__recto">
            <img
              className="card-member-layout__card-background"
              src={getCloudinaryOptimizedUrl(rectoUrl, {
                contentType: "card",
              })}
              alt="Front of membership card"
            />
            <div className="card-member-layout__card-content">
              <div
                data-testid="card-member-layout__personal-info"
                className="card-member-layout__personal-info card-member-layout__card-content-text">
                <span data-testid="card-member-layout__name">{fullName}</span>
                {company && <span data-testid="card-member-layout__company">{company}</span>}
              </div>
              {expirationDate && (
                <span
                  data-testid="card-member-layout__date"
                  className="card-member-layout__date card-member-layout__card-content-text">
                  {expirationDate}
                </span>
              )}
            </div>
          </div>
          <div data-testid="card-member-layout__verso" className="card-member-layout__verso">
            <img
              className="card-member-layout__card-background"
              src={getCloudinaryOptimizedUrl(versoUrl, {
                contentType: "card",
              })}
              alt="Back of membership card"
            />
            <div className="card-member-layout__card-content">
              <span
                data-testid="card-member-layout__external-id"
                className="card-member-layout__external-id card-member-layout__card-content-text">
                {subscriptionExternalId}
              </span>
            </div>
          </div>
        </div>
        {description && (
          <div
            data-testid="card-member-layout__description"
            className="card-member-layout__description body-text-6"
            // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
          />
        )}
        <div className="card-member-layout__download">
          <div className="card-member-layout__download-pdf">
            <p className="card-member-layout__download-pdf-description">
              {t("common.card-member.download.description.desktop")}
            </p>
            <Button
              testID="card-member-layout__pdf"
              label={t("common.card-member.download.pdf")}
              size={isMobileSize ? "full" : "large"}
              loading={isDownloadPdfLoading}
              onClick={downloadPDF}
            />
          </div>
          {isAppleDevice() && (
            <div className="card-member-layout__download-pass">
              <p className="card-member-layout__download-pass-description">
                {t("common.card-member.download.description.apple.desktop")}
              </p>
              <img
                className="card-member-layout__download-pass-button"
                src={walletButton}
                onClick={addToWallet}
              />
              {isAddToWalletLoading && (
                <div className="card-member-layout__download-pass-loader">
                  <Spin data-testid="card-member-layout__wallet-loader" size="large" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
